// Import the unauthorized callback mechanism from authApi
// We don't need to import setUnauthorizedCallback since we're creating our own callback function

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// We'll use the same callback mechanism that's already set up in authApi
// No need to redefine it here since it's a global callback

// Request/Response types matching the backend DTOs
export interface CreateHabitRequest {
  name: string;
  description?: string;
  iconName: string;
  colorCode: string;
  frequency: 0 | 1 | 2 | 3; // 0=Daily, 1=WeeklySpecificDays, 2=WeeklyCount, 3=IntervalBased
  targetStreakLength: number;
  weeklyDays?: number[]; // For weeklySpecificDays (renamed to match backend)
  weeklyCount?: number; // For weeklyCount
  intervalDays?: number; // For intervalBased
}

export interface UpdateHabitRequest {
  name?: string;
  description?: string;
  iconName?: string;
  colorCode?: string;
  frequency?: 0 | 1 | 2 | 3; // 0=Daily, 1=WeeklySpecificDays, 2=WeeklyCount, 3=IntervalBased
  targetStreakLength?: number;
  isActive?: boolean;
  weeklyDays?: number[]; // For weeklySpecificDays (renamed to match backend)
  weeklyCount?: number; // For weeklyCount
  intervalDays?: number; // For intervalBased
}

export interface HabitResponse {
  id: string;
  name: string;
  description?: string;
  iconName: string;
  colorCode: string;
  frequency: 0 | 1 | 2 | 3; // 0=Daily, 1=WeeklySpecificDays, 2=WeeklyCount, 3=IntervalBased
  targetStreakLength: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  
  // Frequency-specific properties
  weeklyDays?: number[]; // Renamed to match backend
  weeklyCount?: number;
  intervalDays?: number;
  
  // Computed properties
  currentStreak: number;
  longestStreak: number;
  totalCompletions: number;
  completionRate: number;
  isCompletedToday: boolean;
  lastCompletedDate?: string;
}

export interface IconOption {
  name: string;
}

export interface ColorOption {
  name: string;
  code: string;
}

export interface DeleteHabitResponse {
  success: boolean;
  message: string;
  updatedStats: DashboardStatsResponse;
}

export interface DashboardStatsResponse {
  totalHabits: number;
  activeStreaks: number;
  completedToday: number;
  longestStreak: number;
  weeklyCompletionRate: number;
}

export interface ToggleHabitCompletionRequest {
  habitId: string;
  completedDate?: string;
  notes?: string;
}

export interface HabitCompletionResponse {
  id: string;
  habitId: string;
  completedDate: string;
  notes?: string;
  createdAt: string;
}

export interface ToggleCompletionResponse {
  success: boolean;
  message: string;
  isCompleted: boolean;
  completion?: HabitCompletionResponse;
  updatedStats: DashboardStatsResponse;

  // Streak stats for the habit - eliminates need for extra API call
  currentStreak?: number;
  longestStreak?: number;
  completionRate?: number;
}

// Get the unauthorized callback from the auth store
let onUnauthorized: (() => void) | null = null;

// Function to set the callback (will be called from authStore)
export const setHabitApiUnauthorizedCallback = (callback: () => void) => {
  onUnauthorized = callback;
};

class HabitApiService {
  private readonly baseUrl = `${API_BASE_URL}/habits`;

  private async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(endpoint, config);
      
      if (!response.ok) {
        // Handle 401 Unauthorized specifically
        if (response.status === 401) {
          // Call the unauthorized callback if we have one and we had a token
          if (authToken && onUnauthorized) {
            console.warn('Token expired or invalid, clearing auth state');
            onUnauthorized();
          }
          throw new Error('Authentication required');
        }

        // Handle 402 Payment Required (habit limit exceeded)
        if (response.status === 402) {
          try {
            const errorData = await response.json();
            const habitLimitError = new Error(errorData.message || 'Habit limit exceeded');
            (habitLimitError as any).status = 402;
            (habitLimitError as any).isHabitLimitExceeded = true;
            (habitLimitError as any).currentCount = errorData.currentCount;
            (habitLimitError as any).maxAllowed = errorData.maxAllowed;
            (habitLimitError as any).subscriptionTier = errorData.subscriptionTier;
            (habitLimitError as any).upgradeRequired = errorData.upgradeRequired;
            throw habitLimitError;
          } catch {
            const habitLimitError = new Error('Habit limit exceeded');
            (habitLimitError as any).status = 402;
            (habitLimitError as any).isHabitLimitExceeded = true;
            throw habitLimitError;
          }
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.Message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }
        
        throw new Error(errorMessage);
      }

      // Handle empty responses (like delete)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return {} as T;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async createHabit(request: CreateHabitRequest): Promise<HabitResponse> {
    const response = await this.makeAuthenticatedRequest<HabitResponse>(
      this.baseUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    return response;
  }

  async getHabits(includeInactive: boolean = false): Promise<HabitResponse[]> {
    const url = `${this.baseUrl}?includeInactive=${includeInactive}`;
    const response = await this.makeAuthenticatedRequest<HabitResponse[]>(url);
    return response;
  }

  async getHabit(id: string): Promise<HabitResponse> {
    const response = await this.makeAuthenticatedRequest<HabitResponse>(
      `${this.baseUrl}/${id}`
    );
    return response;
  }

  async updateHabit(id: string, request: UpdateHabitRequest): Promise<HabitResponse> {
    const response = await this.makeAuthenticatedRequest<HabitResponse>(
      `${this.baseUrl}/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    return response;
  }

  async deleteHabit(id: string): Promise<DeleteHabitResponse> {
    const response = await this.makeAuthenticatedRequest<DeleteHabitResponse>(
      `${this.baseUrl}/${id}`,
      {
        method: 'DELETE',
      }
    );
    return response;
  }

  async getAvailableIcons(): Promise<string[]> {
    const response = await this.makeAuthenticatedRequest<string[]>(
      `${this.baseUrl}/icons`
    );
    return response;
  }

  async getSuggestedColors(): Promise<ColorOption[]> {
    const response = await this.makeAuthenticatedRequest<ColorOption[]>(
      `${this.baseUrl}/colors`
    );
    return response;
  }

  async getDashboardStats(): Promise<DashboardStatsResponse> {
    const response = await this.makeAuthenticatedRequest<DashboardStatsResponse>(
      `${this.baseUrl}/dashboard-stats`
    );
    return response;
  }

  async toggleHabitCompletion(request: ToggleHabitCompletionRequest): Promise<ToggleCompletionResponse> {
    const response = await this.makeAuthenticatedRequest<ToggleCompletionResponse>(
      `${API_BASE_URL}/habitcompletions/toggle`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    return response;
  }

  async getUserCompletions(): Promise<HabitCompletionResponse[]> {
    const response = await this.makeAuthenticatedRequest<HabitCompletionResponse[]>(
      `${API_BASE_URL}/habitcompletions`
    );
    return response;
  }

  async getHabitCompletions(habitId: string): Promise<HabitCompletionResponse[]> {
    const response = await this.makeAuthenticatedRequest<HabitCompletionResponse[]>(
      `${API_BASE_URL}/habitcompletions/habit/${habitId}`
    );
    return response;
  }
}

// Export singleton instance
export const habitApiService = new HabitApiService(); 